/**
 * ScrollReveal - Scroll-triggered reveal animation
 *
 * Features:
 * - 5 reveal directions: up, down, left, right, none
 * - Optional blur transition for depth effect
 * - Configurable threshold and timing
 * - Performance optimized
 */

import { useRef } from "react";
import { motion, useInView, type Variants } from "framer-motion";
import { cn } from "../../utils";

export interface ScrollRevealProps {
  children: React.ReactNode;
  className?: string;
  /** Direction element appears from. Default: "up" */
  direction?: "up" | "down" | "left" | "right" | "none";
  /** Animation delay. Default: 0 */
  delay?: number;
  /** Animation duration. Default: 0.6s */
  duration?: number;
  /** Distance to travel. Default: 40px */
  distance?: number;
  /** Only animate once. Default: true */
  once?: boolean;
  /** Viewport threshold to trigger. Default: 0.2 */
  threshold?: number;
  /** Enable blur transition. Default: false */
  blur?: boolean;
}

const getDirectionOffset = (direction: string, distance: number) => {
  switch (direction) {
    case "up":
      return { y: distance };
    case "down":
      return { y: -distance };
    case "left":
      return { x: distance };
    case "right":
      return { x: -distance };
    default:
      return {};
  }
};

export function ScrollReveal({
  children,
  className = "",
  direction = "up",
  delay = 0,
  duration = 0.6,
  distance = 40,
  once = true,
  threshold = 0.2,
  blur = false,
}: ScrollRevealProps) {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once, amount: threshold });

  const variants: Variants = {
    hidden: {
      opacity: 0,
      filter: blur ? "blur(10px)" : "blur(0px)",
      ...getDirectionOffset(direction, distance),
    },
    visible: {
      opacity: 1,
      filter: "blur(0px)",
      x: 0,
      y: 0,
    },
  };

  return (
    <motion.div
      ref={ref}
      className={cn(className)}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={variants}
      transition={{
        duration,
        delay,
        ease: [0.25, 0.4, 0.25, 1],
      }}
    >
      {children}
    </motion.div>
  );
}

ScrollReveal.displayName = "ScrollReveal";
